import { html, TemplateResult } from "lit";
import { customElement, property } from "lit/decorators.js";
import { type AccentColor, FilterElement, Label } from "@/components/search/filter_element";
import { FilterCollection } from "@/components/search/filter_collection";
import { searchQueryState } from "@/state/SearchQuery";
import { I18n } from "@/i18n/i18n";

/**
 * This component inherits from FilterCollectionElement.
 * It represent a lists of dismissible tokens, one for each active label
 *
 * @element d-search-token
 *
 * @prop {AccentColor} color - The color associated with the filter
 * @prop {string} param - The searchQuery param to be used for this filter
 * @prop {boolean} multi - Whether one or more labels can be selected at the same time
 * @prop {(l: Label) => string} paramVal - A function that extracts the value that should be used in a searchQuery for a selected label
 * @prop {[Label]} labels - All labels that could potentially be selected
 */
@customElement("d-search-token")
export class SearchToken extends FilterElement {
    @property({ type: String })
    color: AccentColor;

    private onRemoveClick(e: Event): void {
        e.preventDefault();

        const btn = e.currentTarget as HTMLElement;
        const id = btn.dataset.labelId;
        const label = this.labels.find((l) => l.id === id);

        this.unSelect(label);
    }

    processClick(e: Event, label: Label): void {
        this.unSelect(label);
        e.preventDefault();
    }

    render(): TemplateResult {
        return html`
            ${this.getSelectedLabels().map((label) => {
                const removeLabel = `Remove ${label.name}`;

                return html`
                    <div class="token accent-${this.color}">
                        <span class="token-label">${label.name}</span>
                        <button
                            type="button"
                            class="close"
                            data-label-id="${label.id}"
                            aria-label="${removeLabel}"
                            title="${removeLabel}"
                            @click=${this.onRemoveClick}
                        >
                            <i class="mdi mdi-close mdi-18" aria-hidden="true"></i>
                        </button>
                    </div>
                `;
            })}
        `;
    }
}

/**
 * This component represents a list of d-search-token
 *
 * @element d-search-tokens
 *
 * @prop {FilterOptions} filters - The dropdowns and switches for which tokens could be created
 * @prop {string[]} hide - The list of filter params that should never be shown
 */
@customElement("d-search-tokens")
export class SearchTokens extends FilterCollection {
    onClearAllClick(e: Event): void {
        e.preventDefault();
        this.clearAll();
    }

    get activeDropdowns(): number {
        let count = 0;
        this.visibleDropdowns.forEach((f) => {
            if (f.multi) {
                count += searchQueryState.arrayQueryParams.get(f.param)?.length || 0;
            } else {
                count += searchQueryState.queryParams.get(f.param) ? 1 : 0;
            }
        });
        return count;
    }

    clearAll(): void {
        this.visibleDropdowns.forEach((f) => {
            if (f.multi) {
                searchQueryState.arrayQueryParams.set(f.param, []);
            } else {
                searchQueryState.queryParams.set(f.param, undefined);
            }
        });
    }

    render(): TemplateResult {
        if (!this.visibleDropdowns) {
            return html``;
        }

        return html`
            ${this.visibleDropdowns.map(
                (c) => html`
                    <d-search-token .labels=${c.data} .color=${c.color} .param=${c.param} .multi=${c.multi}>
                    </d-search-token>
                `,
            )}
            ${
                this.activeDropdowns > 0
                    ? html`
                          <div class="help-block ms-1">
                              ${I18n.t("js.search.tokens.active_filters", { smart_count: this.activeDropdowns })}
                              <a
                                  href=""
                                  role="button"
                                  @click=${this.onClearAllClick}
                                  aria-label="${I18n.t("js.search.tokens.clear")}"
                              >
                                  ${I18n.t("js.search.tokens.clear")}
                              </a>
                          </div>
                      `
                    : html``
            }
        `;
    }
}
